<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { SENTIMENT_STYLES, normalizeSentiment } from './sentiment';

const props = defineProps({
  markup: { type: Object, required: true },
});

const { t, locale } = useI18n();
const labels = useMapGetter('labels/getLabels');

const sentiment = computed(() => normalizeSentiment(props.markup.sentiment));
const style = computed(() => SENTIMENT_STYLES[sentiment.value]);

const score = computed(() => {
  const value = Number(props.markup.score);
  return Number.isNaN(value) ? null : Math.max(-1, Math.min(1, value));
});

const markerPosition = computed(() => ((score.value + 1) / 2) * 100);

const formattedScore = computed(() =>
  score.value === null
    ? ''
    : `${score.value > 0 ? '+' : ''}${score.value.toLocaleString(locale.value, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
);

const tags = computed(() =>
  (props.markup.tags || []).map(tag => {
    const label = labels.value.find(item => item.title === tag);
    return { title: tag, color: label?.color || '#64748B' };
  })
);

const rules = computed(() =>
  (props.markup.rules_matched || []).map(rule =>
    typeof rule === 'string' ? rule : rule.name || rule.id
  )
);

const processedAt = computed(() => {
  if (!props.markup.processed_at) return '';
  return new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(props.markup.processed_at));
});

const latency = computed(() => {
  const ms = Number(props.markup.latency_ms);
  if (!ms) return '';
  return ms >= 1000
    ? t('MULTICHAT.AI_PANEL.SECONDS', { value: (ms / 1000).toFixed(1) })
    : t('MULTICHAT.AI_PANEL.MILLISECONDS', { value: ms });
});
</script>

<template>
  <div class="flex flex-col gap-3 p-3">
    <div
      v-if="markup.spam"
      class="flex items-start gap-2 px-3 py-2 border rounded-lg bg-n-ruby-2 border-n-ruby-4"
    >
      <span
        class="i-lucide-shield-alert size-4 text-n-ruby-11 flex-shrink-0 mt-px"
      />
      <div class="flex flex-col min-w-0 gap-0.5 text-xs">
        <span class="font-semibold text-n-ruby-11">
          {{ t('MULTICHAT.AI_PANEL.SPAM') }}
        </span>
        <span v-if="markup.spam_reason" class="text-n-slate-11">
          {{ markup.spam_reason }}
        </span>
      </div>
    </div>

    <div v-if="style" class="flex flex-col gap-2">
      <div class="flex items-center justify-between gap-2">
        <span class="text-xs text-n-slate-11">
          {{ t('MULTICHAT.SENTIMENT.TITLE') }}
        </span>
        <span
          class="inline-flex items-center gap-1 h-6 px-2 rounded-md text-xs font-semibold"
          :class="style.badgeClass"
        >
          <span class="size-3.5" :class="style.icon" />
          {{ t(style.label) }}
          <span v-if="formattedScore" class="font-mono font-medium opacity-80">
            {{ formattedScore }}
          </span>
        </span>
      </div>
      <div v-if="score !== null" class="flex flex-col gap-1">
        <div
          class="relative h-1.5 rounded-full bg-gradient-to-r from-n-ruby-7 via-n-slate-6 to-n-teal-7"
        >
          <span
            class="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 size-3 rounded-full border-2 border-n-solid-1 shadow"
            :class="style.barClass"
            :style="{ left: `${markerPosition}%` }"
          />
        </div>
        <div class="flex justify-between text-[10px] text-n-slate-10 font-mono">
          <span>{{ t('MULTICHAT.AI_PANEL.SCALE_NEGATIVE') }}</span>
          <span>{{ t('MULTICHAT.AI_PANEL.SCALE_NEUTRAL') }}</span>
          <span>{{ t('MULTICHAT.AI_PANEL.SCALE_POSITIVE') }}</span>
        </div>
      </div>
    </div>

    <div v-if="tags.length" class="flex flex-col gap-1.5">
      <span class="text-xs text-n-slate-11">
        {{ t('MULTICHAT.AI_PANEL.TAGS') }}
      </span>
      <div class="flex flex-wrap gap-1">
        <span
          v-for="tag in tags"
          :key="tag.title"
          class="inline-flex items-center gap-1 h-6 px-2 rounded-md border border-n-weak bg-n-alpha-1 text-xs text-n-slate-12"
        >
          <span
            class="rounded-full size-2"
            :style="{ background: tag.color }"
          />
          {{ tag.title }}
        </span>
      </div>
    </div>

    <div v-if="markup.summary" class="flex flex-col gap-1">
      <span class="text-xs text-n-slate-11">
        {{ t('MULTICHAT.AI_PANEL.SUMMARY') }}
      </span>
      <p
        class="m-0 px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-[13px] leading-5 text-n-slate-12"
      >
        {{ markup.summary }}
      </p>
    </div>

    <div v-if="rules.length" class="flex flex-col gap-1.5">
      <span class="text-xs text-n-slate-11">
        {{ t('MULTICHAT.AI_PANEL.RULES') }}
      </span>
      <div class="flex flex-wrap gap-1">
        <span
          v-for="rule in rules"
          :key="rule"
          class="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-n-amber-3 text-n-amber-11 text-xs font-medium"
        >
          <span class="i-lucide-shield size-3" />
          {{ rule }}
        </span>
      </div>
    </div>

    <div
      class="flex flex-wrap items-center gap-x-3 gap-y-1 pt-2 border-t border-n-weak text-[11px] text-n-slate-10"
    >
      <span v-if="markup.model" class="inline-flex items-center gap-1">
        <span class="i-lucide-cpu size-3" />
        <span class="font-mono">{{ markup.model }}</span>
      </span>
      <span v-if="latency" class="inline-flex items-center gap-1">
        <span class="i-lucide-zap size-3" />
        {{ latency }}
      </span>
      <span v-if="processedAt" class="inline-flex items-center gap-1">
        <span class="i-lucide-clock size-3" />
        {{ processedAt }}
      </span>
    </div>
  </div>
</template>
